// Backup and restore for the Packing list. The tool derives its visible list from code (presets) plus saved extras,
// and only writes a key once it changes, so a bare key dump could miss the list. This adds one self-contained
// snapshot (trip types + the full item list + packed flags) that is written on every change and rebuilt on restore.
// Old backup files (no snapshot) restore exactly as before. Everything stays on the device.

export const PRESETS: Record<string, string[]> = {
  Beach: [
    "Swimsuit",
    "Sunscreen",
    "Sunglasses",
    "Flip-flops",
    "Beach towel",
    "Hat",
    "After-sun lotion",
    "Waterproof phone case",
  ],
  Business: [
    "Laptop + charger",
    "Business cards",
    "Formal outfits",
    "Dress shoes",
    "Portfolio/notebook",
    "Phone charger",
    "Travel adapter",
  ],
  Camping: [
    "Tent",
    "Sleeping bag",
    "Flashlight/headlamp",
    "First aid kit",
    "Insect repellent",
    "Multi-tool",
    "Water bottle",
    "Fire starter",
  ],
  International: [
    "Visa documents",
    "Travel adapter",
    "Copies of documents",
    "Travel insurance info",
    "Phrasebook/translation app",
  ],
  Winter: [
    "Warm coat",
    "Gloves",
    "Scarf",
    "Thermal layers",
    "Winter boots",
    "Hand warmers",
    "Lip balm",
  ],
};

export const COMMON_ESSENTIALS = [
  "Phone charger",
  "Toiletries",
  "Medications",
  "ID / Passport",
  "Cash and cards",
];

export const PACKING_LIST_KEY = "fdp-packing-list-v2";
const K = { types: "fdp-packing-types-v1", checked: "fdp-packing-checked-v1", extra: "fdp-packing-extra-v1", custom: "fdp-packing-custom-types-v1" };

export interface PackingSnapshot { v: 2; tripTypes: string[]; items: string[]; checked: string[]; customItems: string[]; customTripTypes: Record<string, string[]> }

const isStrArr = (x: unknown): x is string[] => Array.isArray(x) && x.every((i) => typeof i === "string");
const dedupe = (list: string[]) => { const seen = new Set<string>(); return list.filter((i) => { const k = i.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; }); };

/** The list the page shows: essentials, then each selected type's items, then the user's own, without duplicates. */
export function resolveItems(tripTypes: string[], customTripTypes: Record<string, string[]>, customItems: string[]): string[] {
  return dedupe([...COMMON_ESSENTIALS, ...tripTypes.flatMap((t) => customTripTypes[t] ?? PRESETS[t] ?? []), ...customItems]);
}

export function buildPackingSnapshot(tripTypes: string[], customTripTypes: Record<string, string[]>, customItems: string[], checked: string[]): PackingSnapshot {
  return { v: 2, tripTypes, items: resolveItems(tripTypes, customTripTypes, customItems), checked, customItems, customTripTypes };
}

/**
 * Turn a parsed backup's entries into the keys the page reads. With a snapshot, every item in it is guaranteed to
 * come back (anything the presets would not produce is kept as an own item) and packed flags are restored.
 * Without one (old files), the entries are returned untouched.
 */
export function restorePackingEntries(entries: Record<string, unknown>): Record<string, unknown> {
  const s = entries[PACKING_LIST_KEY] as Partial<PackingSnapshot> | undefined;
  if (!s || typeof s !== "object" || !isStrArr(s.items) || !isStrArr(s.checked) || !isStrArr(s.tripTypes)) {
    const { [PACKING_LIST_KEY]: _drop, ...rest } = entries; // unreadable snapshot: fall back to the plain keys
    return rest;
  }
  const custom: Record<string, string[]> = {};
  if (s.customTripTypes && typeof s.customTripTypes === "object") for (const [k, v] of Object.entries(s.customTripTypes)) if (isStrArr(v)) custom[k] = v;
  const tripTypes = s.tripTypes.length ? s.tripTypes : ["Beach"];
  const base = new Set(resolveItems(tripTypes, custom, []).map((i) => i.toLowerCase()));
  const customItems = dedupe([...(isStrArr(s.customItems) ? s.customItems : []), ...s.items]).filter((i) => !base.has(i.toLowerCase()));
  return { ...entries, [K.types]: tripTypes, [K.checked]: s.checked, [K.extra]: customItems, [K.custom]: custom };
}
